import { type Translations } from "@/features/i18n/translations/types";

const en: Translations = {
  common: {
    brand_name: "Development Pyramid",
    child_development_assistance: "Child development assistance",
    child_name: "Child name",
    child_gender: "Child gender",
    child_age: "Child age",
    tell_about_child:
      "Tell us about your child so we can create an individual test to assess their development.",
    enter_name: "Enter name",
    continue: "Continue",
    back: "Back",
    girl: "Girl",
    boy: "Boy",
    year: {
      one: "year",
      few: "years",
      many: "years",
      short_unit: "y.",
    },
    month: {
      one: "month",
      few: "months",
      many: "months",
      short_unit: "mo.",
    },
    year_dative_one: "year",
    year_dative_few: "years",
    month_dative_one: "month",
    month_dative_few: "months",
    name_required: "Name is required",
    name_too_long: "Name must be at most 10 characters",
    name_invalid_chars: "Name can only contain letters and spaces",
    email_required: "Email is required",
    email_invalid: "Please enter a valid email",
    email_too_long: "Email is too long (max\u00A0100 characters)",
    email_invalid_chars: "Email contains invalid characters",
    error_title: "Error",
    try_again: "Try again",
    not_found: "Not Found",
    error_404_title: "404\u00A0Page Not Found",
    error_404_description: "This page could not be found.",
    error_500_title: "500\u00A0Internal Error",
    error_500_description: "Something went wrong.",
    start_over: "Start the test over",
    error: "Something went wrong, please try again later",
    continue_questionnaire:
      "You previously started taking the survey, would you like to continue?",
    continue_button: "Continue",
    later_button: "Later",
    categoryLabel: {
      FineMotor: "Fine motor skills",
      GrossMotor: "Gross motor skills",
      VisualPerception: "Visual perception",
      Speech: "Speech development",
      HearingPerception: "Hearing perception",
      SocialDevelopment: "Social development",
    },
    submit: "Submit",
    skip: "Skip",
    next: "Next",
    close: "Close",
    print: "Print Report",
    share: "Share Report",
    male: "Male",
    female: "Female",
    start: "Start",
    yes: "Yes",
    free: "Free",
    get_free_button: "Get Free",
    pay_button: "Pay",
    enter_button: "Enter →",
    open_telegram_button: "Open Telegram",
    progress_description:
      "Questions are divided into categories and you can track the test progress. As we go through, we may add clarifying questions",
    question_instruction: "Read the question and answer",
  },
  seo: {
    default_title:
      "Discover Your Child's Development Level — Free | Development Pyramid",
    default_description:
      "Identify gaps in your child's development, discover their strengths, and get personalized recommendations. $100 → Free. Start assessment.",
    fallback_description:
      "Comprehensive assessment of child development 0–3\u00A0years based on modern scientific data",
    organization_description:
      "Online platform for child development assessment 0–3\u00A0years",
    app_category: "Educational Application",
    author_job_title: "Child Development Specialist",
    author_name: "Darina Kopenkova",
    og_image_alt:
      "Development Pyramid - child development assessment 0–3\u00A0years",
    language_code: "en",
    locale: "en_US",
  },
  pages: {
    not_found: "404 - Page not found",
  },
  landing: {
    hero_title:
      "<b>Identify gaps in</b> your child's development, discover their <b>strengths</b>, and get <b>personalized recommendations</b>.",
    price: "$100",
    free_for_limited_time: "Free for a limited time",
    comprehensive_diagnosis:
      "A full developmental assessment for ages\u00A00–3 covering key areas: speech, thinking, motor skills, emotions, senses, creativity, and adaptation.",
    step_1: "Step\u00A01",
    step_2: "Step\u00A02",
    step_3: "Step\u00A03",
    faq_link: "FAQ",
    sample_report_link: "Sample Report",
    social_subscribe_text:
      "Learn more about child development, skill-building games, and practical tips",
    questions_title:
      "Child development — endless questions, doubts, and the search for answers",
    sample_report: "Sample Report",
    view_sample_report: "View Sample Report",
    report_example_section_description:
      "Check out a sample report to see how detailed and personalized your child's assessment will be",
    comprehensive_diagnosis_title: "Comprehensive assessment of",
    comprehensive_diagnosis_subtitle: "development for children aged\u00A00–3",
    comprehensive_diagnosis_based: "Grounded in the latest scientific research",
    diagnosis_words_intellectual: "intellectual",
    diagnosis_words_physical: "physical",
    diagnosis_words_social: "social",
    diagnosis_words_emotional: "emotional",
    diagnosis_words_speech: "speech",
    diagnosis_words_sensorimotor: "sensorimotor",
    diagnosis_words_adaptive: "adaptive",
    diagnosis_words_creative: "creative",
    question_1: "Is my child developing on track?",
    question_2: "Should they already be talking by now?",
    question_3: "Sitting without support?",
    question_4: "Crawling already?",
    question_5: "Standing with support at this age?",
    question_6: "Should they understand what I say?",
    question_7: "Eating on their own?",
    question_8: "Asking questions already?",
    questions_description:
      "Put an end to guessing and worrying.<br/>Get a clear picture of your child's development<br/>by answering a few simple questions.",
    start_diagnosis: "Start assessment",
    step_1_text: "Specify child's gender and age",
    step_2_text: 'Answer "Yes" or "No" to personalized questions',
    step_3_text: "Get a unique report",
    result_title:
      "Get an overall development assessment with detailed analysis of key areas, visual charts, and personalized recommendations.",
    benefits_title:
      "Gain peace of mind, confidence, and a clear action plan knowing:",
    benefit_1:
      "How your child's development compares to typical ages in each key area",
    benefit_2:
      "How evenly your child's speech, motor skills, thinking, and emotions are developing",
    benefit_3:
      "Where your child is ahead — and where extra support will help most",
    benefit_4:
      "What steps to take next — with clear, easy-to-follow recommendations",
    faq_title: "We answer parents' most common questions:",
    faq_1_q: "From what age can the assessment be taken?",
    faq_1_a:
      "Suitable for children from birth to 3\u00A0years. The questions are adapted for each age and stage of development.",
    faq_2_q: "How accurate and reliable is the assessment?",
    faq_2_a:
      "The questions and structure are based on proven scientific methods of early development and adapted by specialists. You'll receive a high-quality guide — not random advice from the internet.",
    faq_3_q: "Is this an online test? Do I need to download anything?",
    faq_3_a:
      "Everything is fully online. No downloads or installations — just answer questions in a convenient format.",
    faq_4_q: "How long does it take to complete the assessment?",
    faq_4_a:
      "It usually takes about 10–20\u00A0minutes, depending on your child's age and characteristics. The questions are simple and clear.",
    faq_5_q: "What will be in the report?",
    faq_5_a:
      "A detailed assessment across all key areas, with visual charts and personalized, age-specific recommendations.",
    faq_6_q: "Can I show the report to a specialist?",
    faq_6_a:
      "Yes. The report can easily be saved, printed, or shared. You can discuss it with a pediatrician, neurologist, psychologist, or educator.",
    faq_7_q: "Is this safe? What do you do with my data?",
    faq_7_a:
      "All data is securely protected. We never share it with third parties and use it only to generate your results and improve the service.",
    faq_8_q:
      'My child doesn\'t have "typical" development — should I still take the assessment?',
    faq_8_a:
      "Yes. The assessment helps you understand your child's current developmental level and provides recommendations suited to their individual pace and characteristics.",
    faq_9_q: "What if I answer something wrong?",
    faq_9_a:
      "No problem — the questions are simple and intuitive. The report isn't a medical diagnosis, but a practical guide for understanding and supporting your child's growth.",
    sharing_1: "The report is easy to save, share, or print.",
    sharing_2:
      "Share your child's achievements with family or show them to a specialist.",
    personal_plan_title:
      "Would you like a personalized development plan for your child based on this assessment?",
    app_announcement_title:
      "We're launching an app with a simple, adaptable development program for your child.",
    app_announcement_description:
      "Spend just 15–30\u00A0minutes a day on simple, engaging games — and your child will progress faster in speech, movement, thinking, and more.",
    it_works_title:
      "It works — when skills are developed step by step and at the right time, while the brain and body are most ready to learn.",
    it_works_description:
      "Stop wasting time and money on random activities or scattered advice. No more guessing how to engage your child or whether you're doing it right. Spend time together — with purpose, joy, and confidence. Developmental games will finally come to life!",
    it_works_alt: "Baby on hand",
    copyright: "All rights reserved.",
    privacy_policy: "Privacy Policy",
    terms_of_use: "Terms of Use",
    support: "Support",
    mobile_menu: {
      faq: "Frequently Asked Questions",
    },
  },
  questionnaire: {
    error: "Something went wrong, please try again later or start over",
    question_progress: "{{current}} of {{total}}",
    answer_no: "No",
    answer_not_sure: "Not sure?",
    answer_pending: "Please wait, we're processing your previous answer",
    answer_error: "Something went wrong, please try again",
    completedTitle: "Done!",
    completedSubtitle1: "Please enter your email.",
    welcome_title: "🚀 Unlock your child's potential",
    welcome_description:
      "📈 Conduct a skills assessment and get a personalized development program",
    how_it_works: "How it works:",
    step_1: "1.\u00A0Answer simple questions.",
    step_2:
      "2.\u00A0Get a report and personalized activity schedule immediately.",
    step_3: "3.\u00A0Follow simple instructions for each activity.",
    time_section: "Time:",
    time_questionnaire:
      "Filling out the questionnaire takes 5–10\u00A0minutes.",
    time_activities: "Spend 1–2\u00A0hours a day with your child.",
    result_section: "Result:",
    result_report: "Detailed report.",
    result_schedule: "Individual schedule.",
    result_tips: "Useful tips.",
    result_guarantee: "Guaranteed result.",
    learn_more: "Learn more",
    get_acquainted: "Let's get acquainted!",
    get_acquainted_description:
      "Answer a few questions so we can create an individual test for your baby's development diagnostics",
    specify_gender: "Specify child's gender",
    specify_age: "Specify baby's age",
    months: "months",
    completedSubtitle2: "We will send the test results.",
    email: {
      placeholder: "Your email",
      submit: "Get Results",
      verification1:
        "We sent an email with a link to the test results to your address",
      verification2:
        "If you did not receive the email, please check your spam folder or reach out to us at",
      resend_email: "Resend verification email",
      rate_limit: "Please wait {{seconds}}\u00A0seconds before trying again",
      email_sent_again: "Verification email sent again",
      invalid: "Please enter a valid email",
      delivery_time: "Emails usually arrive within 2‑5\u00A0minutes",
      check_spam: "💡 Check your spam folder",
      change_or_resend: "Change email or resend",
    },
  },
  results: {
    mdHeaderTitle: "Development Assessment Results",
    age_gauge_description: "Developmental age equivalent",
    read_more: "Read more",
    app_announcement_success:
      "Great! You're on the list of first users who will get access to the program. We will definitely notify you when it's ready.",
    app_announcement_description:
      "Now take the diagnostic to get a plan based on the results when the app is ready",
    start_test: "Start New Assessment",
    want_personal_plan:
      "Would you like to get a personal development plan for your child based on this analysis?",
    want_personal_plan_button: "I want a personal plan",
    personal_plan_button: "Get Personal Plan",
    start_diagnostic_button: "Start diagnostic",
    want_app_form: {
      title: "Get a personal development plan",
      description:
        "Leave your email and we'll be the first to notify you when the app with a personal development plan launches",
      email_placeholder: "Your email",
      comment_placeholder: "Any comment (optional)",
      submit: "Get Plan",
    },
  },
  feedback: {
    dialog_title: "How was your experience?",
    dialog_subtitle: "Your feedback helps us improve",
    optional_comment: "Optional: Tell us more about your experience…",
  },
  test: {
    consent_text:
      "I consent to the processing of my child's personal data (name, gender, age, diagnostic results) and my personal data (email) for conducting development diagnostics, forming personalized recommendations, sending results to email and informing about new service capabilities in accordance with <a href='/privacy-policy'>Privacy Policy</a> and <a href='/terms-of-use'>Terms of Use</a>",
    consent_required: "Please accept consent to continue",
    promo_invalid: "Promo code is invalid or already used",
  },
  promo: {
    generate_button: "Generate",
    copy_button: "Copy",
    copied_new: "New promo code copied",
    generated: "Promo code created",
  },
  payment: {
    header_title: "Discover your child's strengths",
    header_description:
      "10\u00A0minute assessment — get a personalized development plan",
    card_title_telegram: "Telegram",
    dialog_step_1: "Open this post in Telegram",
    dialog_step_2: "Leave any comment under it",
    dialog_step_3: "Subscribe to the channel",
    helper_text: "Subscribe to Telegram → Comment → Get link in DM",
    caption_text: "Link with promo code will come to private messages",
    icon_comment: "Comment",
    icon_code_dm: "Code in DM",
    dialog_title: "How to get free access",
    dialog_step_4: "You'll receive a link to free diagnostics in DM",
    payment_title: "Payment",
    payment_price: "$100",
    payment_caption: "Instant access. Receipt will come to email.",
    or_divider: "OR",
    promo_code_text: "I have a promo code",
    privacy_text: "🔒 Data is not published • Access will be saved forever",
  },
  report: {
    list: {
      need_fast_development: "{{count}} areas require accelerated development",
      mastered_on_time: "{{count}} skills mastered on time",
      mastered_early: "{{count}} skills mastered ahead of peers",
    },
    stability_stable: "Keep it up! Stable age-appropriate development.",
    stability_ahead: "Great! The child is significantly ahead of age norms.",
    stability_unstable:
      "Unstable development: both leading and lagging groups exist — consider a systemic approach.",
    stability_behind:
      "The child slightly lags behind age norms — consider introducing exercises for lagging skills.",
    stability_too_behind:
      "The child significantly lags behind age norms — consider consulting a specialist and introducing targeted activities.",
    stability_too_behind_unstable:
      "A mix of delayed and advanced skills is observed. We recommend a specialist consultation and a structured approach to balanced development.",
    overview: {
      title: "Development by Category",
    },
    legend: {
      mastered: "Mastered",
      early: "Mastered early",
    },
    labels: {
      advanced_mastered: "Mastered in advance",
      matching_mastered: "Mastered on time",
      missing: "Not mastered",
      missing_overdue: "Not yet mastered (past expected age)",
      on_time: "On track",
      advanced: "Advanced skills",
      missed: "Not yet mastered",
    },
    stats: {
      should_be_mastered_label: "Expected by {{age}}",
    },
    words: {
      skill_one: "skill",
      skill_few: "skills",
      skill_many: "skills",
    },
    progress: {
      you_mastered: "Mastered by your child",
    },
    skills: {
      of_them: "Of these",
      to_master: "To develop ({{count}})",
      mastered_on_time: "Mastered on time ({{count}})",
      mastered_in_advance: "Mastered ahead of peers ({{count}})",
    },
    conclusions: {
      excellent_title: "Excellent development",
      excellent_subtitle: "Ahead of age norms:",
      excellent_text:
        "this means that in these categories skills develop faster than expected.",
      well_title: "Good development",
      well_subtitle: "In line with age:",
      well_text: "skills are developing in accordance with age norms.",
      steady_title: "Steady development",
      steady_subtitle: "Requires maintaining pace:",
      steady_text:
        "skills develop steadily, it is important to maintain the current pace.",
      uneven_title: "Uneven development",
      uneven_subtitle: "Requires attention:",
      uneven_text:
        "fluctuations are observed, which requires additional attention.",
      critical_title: "Critical lag",
      critical_subtitle: "Requires urgent attention:",
      critical_text:
        "skills significantly lag behind the norm and require immediate action.",
    },
    cta: {
      title:
        "✨ Would you like to receive a personalized development plan based on this assessment? ✨",
      p1: "🚀 We're launching an app that offers a simple, adaptive, and personalized development program for your child.",
      p2: "⏰ Spend just 15–30\u00A0minutes a day to help your child catch up or get ahead across key areas — thinking, motor skills, and speech.",
      p3: "💡 The program works best when skills are introduced in the right sequence — when the brain and body are most ready to learn.",
      p4: "Say goodbye to random classes and scattered advice — get a clear, science-based plan with specific steps.",
      p5: "🎯 Make playtime truly effective — no more unused developmental toys collecting dust!",
    },
    pie: {
      main_text:
        "<0>{{child}}</0> <1>already mastered <2>{{totalMastered}}</2></1><3><4>, of which <5>{{advancedMastered}}</5> (<6>{{advancedPercent}}%</6>) are mastered ahead of peers</4></3>",
      missing_text:
        "Unfortunately, your child <0>hasn't yet mastered <1>{{missing}}</1> (<2>{{missingPercent}}%</2>)</0> out of <3>{{totalExpected}}</3>. However, there is already a good base to catch up quickly.",
    },
    ai_report: {
      print_title: "Overall development skills report",
      print_error:
        "Printing is not available in this browser. Try using a different browser or copy the text manually.",
      loading_title:
        "Your personalized summary and recommendations will appear here in a few minutes.",
      error_title: "The summary is temporarily unavailable.",
      error_description: "Please refresh the page later or try again.",
      error_retry: "Try again",
      not_ready_title: "Your report is being prepared.",
      not_ready_description:
        "Check back shortly to view your personalized insights.",
      not_ready_refresh: "Refresh",
      mastered_on_time: "mastered on time",
      percent_mastered_on_time: "percent mastered on time",
      missing_required: "missing, but should have been mastered by this age",
      percent_missing_on_time: "percent missing on time",
      mastered_early: "mastered even earlier than normative age",
      percent_mastered_early: "percent mastered earlier than normative age",
      next_to_master: "next to master",
    },
    share: {
      title: "{{name}}'s Development Report",
      text: "See {{name}}'s personalized development results",
    },
    categories_stability: {
      behind_title: "Areas needing attention",
      ahead_title: "Advanced Development",
      balanced_title: "Balanced Development",
      balanced_description:
        "All developmental areas show age-appropriate progress",
      behind_lags: "falls behind age expectations",
      behind_lag: "lags behind age norms",
      behind_with_advanced: "alongside advanced skills in other areas",
      unstable_developed: "showing uneven progress",
      unstable_develop: "developed unevenly",
      stable_sufficient: "sufficiently developed",
      stable_sufficient_plural: "sufficiently developed",
      ahead_ahead: "ahead of age norms",
      ahead_aheads: "ahead of age norms",
      category: "category",
      categories: "categories",
      categories_many: "categories",
      though: "though",
      and: "and",
      and_yet: "and yet",
    },
  },
  tutorial: {
    if_child_can: "if the child can perform the specified action.",
    if_child_cannot:
      "If the child doesn't have or only partially has the skill - click",
    hint_description:
      'If the question is not quite clear or you have doubts, click "not sure" to read the hint with answer criteria for this question.',
    auto_save: "Each answer",
    auto_save_highlight: "is automatically saved in the browser",
    can_close_and_return:
      "You can close the tab at any time and return to continue the test later, starting from the question where you stopped.",
  },
  landing_assets: {
    questionnaire: {
      question_1_name: 'Can follow a request: "Give me two..."',
      question_1_hint:
        'Can your child give a specific number of items when asked (for example, "Give me two blocks", "Give me three balls")? Does your child understand numbers "two", "three" and can follow such requests?',
      question_2_name:
        "Does your child understand the names of many familiar objects, people, and some actions?",
      question_2_hint:
        "Can your child point to or give a familiar object when asked (like 'give me the ball' or 'where is the teddy bear?'), point to a familiar person, or understand simple action names (like 'kiss mommy' or 'wave bye-bye')?",
      question_11_name: "Can express thoughts in short words",
      question_12_name: "Can a child unbutton a button",
      question_13_name:
        "Can interact with different people who become familiar",
    },
    report: {
      child_name: "Kate",
    },
  },
};

export default en;
